import {
  dateTransform,
  fixedDigitsValue,
  getCouponBadgeStatus,
} from "helpers/dataHelpers";

import { CancelIcon, CheckCircleIcon, TextFieldsIcon } from "icons";
import {
  Coupon,
  CouponAllowedUser,
  CouponsFilterNames,
  CouponsFilterTypes,
  CouponType,
  DiscountType,
  DiscountTypeLabel,
} from "types/coupons";
import { theme } from "theme";
import { Badge } from "components";

import { FlexContainer } from "styled/Box";
import { getCouponDiscountLabel } from "pages/Coupons/helpers";
import {
  FilterField,
  FilterFieldDropdownOption,
  FilterOption,
} from "types/tableFilters";

export const columns = [
  {
    title: "Coupon code",
    dataIndex: "couponCode",
    key: "couponCode",
  },
  {
    title: "Bonus Type",
    dataIndex: "discountType",
    key: "discountType",
    render: (value: DiscountType) => DiscountTypeLabel[value] || value,
  },
  {
    title: "Bonus (%)",
    dataIndex: "discountOff",
    key: "discountOff",
    align: "right" as const,
    render: (value: number, record: Coupon) =>
      record.discountType === DiscountType.Percentage ||
      record.couponType === CouponType.DiscountCodeFiat
        ? getCouponDiscountLabel(value, record)
        : null,
  },
  {
    title: "Bonus (FM Coins)",
    dataIndex: "discountOff",
    key: "discountOff",
    align: "right" as const,
    render: (value: number, record: Coupon) =>
      record.discountType === DiscountType.FmcCents
        ? getCouponDiscountLabel(value, record)
        : null,
  },
  {
    title: "KWD entered by admin",
    dataIndex: "discountFiatCents",
    key: "discountFiatCents",
    align: "right" as const,
    width: 300,
    render: (value: number | undefined) =>
      value ? `${fixedDigitsValue(value, 3)} KWD` : null,
  },
  {
    title: "Number of Unique Users",
    dataIndex: "numberOfUniqueUses",
    key: "numberOfUniqueUses",
    align: "center" as const,
    width: 300,
  },
  {
    title: "Number Of Uses (per user)",
    dataIndex: "numberOfUses",
    key: "numberOfUses",
    align: "center" as const,
    width: 300,
  },
  {
    title: "Count Of Uses",
    dataIndex: "couponUsageCount",
    key: "couponUsageCount",
    align: "center" as const,
  },
  {
    title: "Allowed users",
    dataIndex: "allowedUsers",
    key: "allowedUsers",
    align: "center" as const,
    render: (users: CouponAllowedUser[]) => users?.length || "All",
  },
  {
    title: "Active",
    dataIndex: "isActive",
    key: "isActive",
    align: "center" as const,
    render: (isActive: boolean) => (
      <FlexContainer $justify="center">
        {isActive ? (
          <CheckCircleIcon />
        ) : (
          <CancelIcon fill={theme.color.error} />
        )}
      </FlexContainer>
    ),
  },
  {
    title: "Multiple usage",
    dataIndex: "allowMultipleUsage",
    key: "allowMultipleUsage",
    align: "center" as const,
    render: (isActive: boolean) => (
      <FlexContainer $justify="center">
        {isActive ? (
          <CheckCircleIcon />
        ) : (
          <CancelIcon fill={theme.color.error} />
        )}
      </FlexContainer>
    ),
  },
  {
    title: "Status",
    dataIndex: "startDate",
    key: "startDate",
    align: "center" as const,
    render: (startDate: string, record: Coupon) => {
      const { title, bgColor, textColor } = getCouponBadgeStatus(
        startDate,
        record.expiryDate,
      );
      return <Badge title={title} bgColor={bgColor} textColor={textColor} />;
    },
  },

  {
    title: "Start date",
    dataIndex: "startDate",
    key: "startDate",
    render: (date: string) =>
      dateTransform({
        date,
      }),
  },
  {
    title: "Expire date",
    dataIndex: "expiryDate",
    key: "expiryDate",
    render: (date: string) =>
      dateTransform({
        date,
      }),
  },
  {
    title: "Updated at",
    dataIndex: "updatedAt",
    key: "updatedAt",
    render: (date: string) =>
      dateTransform({
        date,
      }),
  },
  {
    title: "Created at",
    dataIndex: "createdAt",
    key: "createdAt",
    render: (date: string) =>
      dateTransform({
        date,
      }),
  },
];

export const discountTypeDropdownFields: FilterFieldDropdownOption[] = [
  {
    type: "percentage",
    label: "Percentage",
  },
  {
    type: "fiatCents",
    label: "FiatCents",
  },
  {
    type: "fmcCents",
    label: "FmcCents",
  },
];

export const filtersList: FilterOption[] = [
  {
    key: CouponsFilterTypes.discountType,
    label: CouponsFilterNames[CouponsFilterTypes.discountType],
    field: FilterField.SELECT,
    options: discountTypeDropdownFields,
  },
  {
    key: CouponsFilterTypes.discountOff,
    icon: <TextFieldsIcon />,
    label: CouponsFilterNames[CouponsFilterTypes.discountOff],
    field: FilterField.INPUT_SELECT,
  },
  {
    key: CouponsFilterTypes.activityDate,
    label: CouponsFilterNames[CouponsFilterTypes.activityDate],
    field: FilterField.DATE,
  },
];
